// --- Dependencies -------------------------------------------------------------------------------
import { useMemo, useState } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import useLang from "@/hooks/useLang";
import usePermission from "@/hooks/usePermission";
import usePopup from "@/hooks/usePopup";
import useResourceTable from "@/hooks/useResourceTable";
import useTranslation from "@/hooks/useTranslation";
import ActionsBar from "@/UI/components/ActionsBar";
import BulkBar from "@/UI/components/BulkBar";
import DataTable from "@/UI/components/DataTable";
import type { TyColumn } from "@/UI/components/DataTable/types";
import ListScreen from "@/UI/components/ListScreen";
import StatsSection from "@/UI/components/StatsSection";
import FilterSelect from "@/UI/inputs/FilterSelect";
import ConfirmPopup from "@/UI/popups/ConfirmPopup";
import type { TyClientRow } from "@/types";
import { formatDate, formatNumber } from "@/utils/format";

// --- Types --------------------------------------------------------------------------------------

type TyStats = { total: number; owners: number };

// --- Constants ----------------------------------------------------------------------------------

const GENDERS = ["male", "female"] as const;

// --- Logic --------------------------------------------------------------------------------------

export default function ClientsScreen() {
    const t = useTranslation();
    const lang = useLang((state) => state.lang);
    const { canEdit } = usePermission();
    const { addComp, removeComp } = usePopup();

    const [search, setSearch] = useState("");
    const [gender, setGender] = useState("");

    const table = useResourceTable<TyClientRow, TyStats>({ path: "clients/", rowsKey: "clients", query: { search, gender } });

    const isEditable = canEdit("clients");

    const genderLabel = (value: string | null): string =>
        value === "male" ? t.clients.genderMale : value === "female" ? t.clients.genderFemale : t.clients.genderUnset;

    const stats = useMemo(() => {
        if (!table.stats) return [];
        return [
            { key: "total", label: t.lists.clients.statTotal, value: formatNumber(table.stats.total, lang), tone: "brand" as const },
            { key: "owners", label: t.lists.clients.statOwners, value: formatNumber(table.stats.owners ?? 0, lang), tone: "good" as const },
        ];
    }, [table.stats, t, lang]);

    const columns = useMemo<TyColumn<TyClientRow>[]>(
        () => [
            {
                key: "name",
                label: t.cols.client,
                width: "minmax(180px, 1.5fr)",
                render: (row) => (
                    <span className="list-cell-avatar-wrap">
                        <span className="list-cell-avatar">
                            {row.picture ? (
                                <img src={row.picture} alt="" />
                            ) : (
                                <img className="list-cell-avatar-fallback" src="/icons/client-icon.svg" alt="" />
                            )}
                        </span>
                        <span className="list-cell-strong">{row.name}</span>
                    </span>
                ),
                edit: isEditable ? { kind: "text", value: (row) => row.name } : undefined,
            },
            {
                key: "phone",
                label: t.cols.phone,
                width: "minmax(140px, 1fr)",
                render: (row) => (
                    <span className="list-cell-mono" dir="ltr">
                        {row.phone}
                    </span>
                ),
                /* Changing this signs every one of their devices out — see the controller. */
                edit: isEditable ? { kind: "text", value: (row) => row.phone } : undefined,
            },
            {
                key: "gender",
                label: t.cols.gender,
                width: "130px",
                render: (row) => <span>{genderLabel(row.gender)}</span>,
                edit: isEditable
                    ? {
                          kind: "select",
                          nullable: true,
                          options: GENDERS.map((one) => ({ value: one, label: genderLabel(one) })),
                          value: (row) => row.gender,
                      }
                    : undefined,
            },
            {
                key: "ownedStores",
                label: t.cols.owned,
                width: "90px",
                align: "center",
                render: (row) => <span>{formatNumber(row.ownedStores, lang)}</span>,
            },
            {
                key: "accessStores",
                label: t.cols.access,
                width: "90px",
                align: "center",
                secondary: true,
                render: (row) => <span>{formatNumber(row.accessStores, lang)}</span>,
            },
            {
                key: "createdAt",
                label: t.cols.created,
                width: "130px",
                secondary: true,
                render: (row) => <span className="list-cell-muted">{formatDate(row.createdAt, lang)}</span>,
            },
        ],
        [t, lang, isEditable]
    );

    const confirmDelete = (ids: string[], label: string) => {
        removeComp("confirm-delete-client");
        addComp({
            name: "confirm-delete-client",
            Component: ConfirmPopup,
            place: "center",
            anim: "scale",
            cover: "shaded",
            coverClose: true,
            details: {
                message: `${t.lists.clients.deleteWarning} — ${label}`,
                icon: "/icons/delete-icon.svg",
                variant: "danger",
                confirmLabel: t.table.deleteConfirm,
                cancelLabel: t.table.deleteCancel,
                onConfirm: () => table.deleteBulk(ids),
            },
        });
    };

    return (
        <ListScreen writeError={table.writeError}>
            <StatsSection loading={table.loading && !table.stats} stats={stats} />

            <ActionsBar
                search={{ value: search, onChange: setSearch, placeholder: t.lists.clients.search }}
                activeFilterCount={gender ? 1 : 0}
                onClearFilters={() => setGender("")}
                filters={
                    <FilterSelect
                        label={t.cols.gender}
                        value={gender}
                        onChange={setGender}
                        options={GENDERS.map((one) => ({ value: one, label: genderLabel(one) }))}
                    />
                }
            />

            <DataTable<TyClientRow>
                rows={table.rows}
                columns={columns}
                rowId={(row) => row._id}
                loading={table.loading}
                error={table.error}
                onRetry={table.refetch}
                pager={{ page: table.page, lastPage: table.lastPage, total: table.total, onPage: table.setPage }}
                selection={isEditable ? { selected: table.selected, onChange: table.setSelected } : undefined}
                onCellSave={isEditable ? (row, key, value) => table.saveCell(row._id, key, value) : undefined}
                rowActions={(row) =>
                    isEditable ? (
                        <button
                            type="button"
                            className="list-row-btn list-row-btn--danger"
                            onClick={() => confirmDelete([row._id], row.name)}
                            aria-label={t.table.deleteRow}
                        >
                            <img src="/icons/delete-icon.svg" alt="" width={15} height={15} />
                        </button>
                    ) : null
                }
            />

            {isEditable && (
                <BulkBar
                    count={table.selected.length}
                    busy={table.busy}
                    fields={[
                        {
                            key: "gender",
                            label: t.cols.gender,
                            kind: "select",
                            options: GENDERS.map((one) => ({ value: one, label: genderLabel(one) })),
                        },
                    ]}
                    onApply={table.applyBulk}
                    onDelete={() => confirmDelete(table.selected, `${table.selected.length}`)}
                    onClear={() => table.setSelected([])}
                />
            )}
        </ListScreen>
    );
}
